'use client';

import { clsx } from 'clsx';
import {
  MouseEventHandler,
  SyntheticEvent,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import { BannerUpsellingStoryblok } from 'storyblok-types';
import { Button } from 'atoms/Button';
import { Icon, IconType } from 'atoms/Icon';
import { Image } from 'atoms/Image/Image';
import { Link } from 'atoms/Link/Link';
import { ParseBBCode } from 'atoms/ParseBBCode';
import { P, Span } from 'atoms/Typography';
import { DeliveryStatus, DeliveryStatusType } from 'molecules/DeliveryStatus';
import { Discount } from 'molecules/Discount';
import { Modal } from 'molecules/Modal';
import { OldPrice } from 'molecules/OldPrice';
import { Price } from 'molecules/Price';
import { ProductBullets } from 'molecules/ProductBullets';
import { ProductImage } from 'molecules/ProductImage';
import { ProductReferencePrice } from 'molecules/ProductReferencePrice';
import { ProductSize } from 'molecules/ProductSize';
import { ReviewsRating } from 'molecules/ReviewsRating';
import { SelectSizeButtons } from 'molecules/SelectSizeButtons';
import { Vat } from 'molecules/Vat';
import { WishlistIcon } from 'molecules/WishlistIcon';
import { ProductMaterialInfo } from 'pages/ProductDetail/ProductMaterialInfo';
import { ProductVideoBlock } from 'pages/ProductDetail/ProductVideoBlock/ProductVideoBlock';
import { UpsellingBannerModal } from 'pages/ProductDetail/UpsellingBannerModal';
import { gtmPushItemDetailClick } from 'modules/gtm/modules/sections/productDetails/productDetails';
import { parseDataToGTMItemViewDetail } from 'modules/gtm/utils/parsers';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { useRouter } from 'modules/next/hooks';
import { useCart } from 'modules/shop/cart/hooks';
import navigationManager from 'modules/shop/navigationManager';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import {
  DAILY_DEAL_PRODUCT_BADGE_URL,
  DAILY_DEAL_PRODUCT_LABEL,
} from 'commerce/scayle/constants';
import { IAdaptedListProduct } from 'commerce/scayle/utils/adapter/types';
import styles from './ProductTile.module.css';

export const ProductTile = forwardRef<
  HTMLDivElement | null,
  {
    product: IAdaptedListProduct;
    gtmCategoryName?: string;
    displaySalePrices?: boolean;
    upsellingBanner?: BannerUpsellingStoryblok;
  }
>(({ product, gtmCategoryName, displaySalePrices, upsellingBanner }, ref) => {
  // reduce amount of elements on listing page
  const [isMounted, setMounted] = useState(false);
  const t = useTranslations();
  const { gtmData } = useSiteConfig();
  const pageType = gtmData?.pageType;
  const { video } = product;
  const { isMobile, isTablet } = useMediaQuery();
  const { addToCart } = useCart();
  const [isSizeSelectVisible, setIsSizeSelectVisible] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const { push } = useRouter();
  const [openBannerModal, setOpenBannerModal] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!product) {
    return null;
  }

  const activeVariantPrice = product.price;

  const hasSizes = product.size.length > 1;
  const hasVideo = video?.length;
  const currentPrice = activeVariantPrice.withTax;

  const oldPrice = activeVariantPrice.recommendedRetailPrice;

  const { category, brand } = product;
  const toggleSizeSelectVisibility = () =>
    setIsSizeSelectVisible(!isSizeSelectVisible);
  const toggleModal = (e: Event | SyntheticEvent<Element, Event>) => {
    e.preventDefault();
    setOpenModal(!openModal);
  };

  const setProductCategoryIdToLocalStorage = async () => {
    if (gtmCategoryName) {
      navigationManager.addStorageCategory(product, gtmCategoryName);
    }
  };

  const pushGtmItemDetailClick = () => {
    const detailViewData = parseDataToGTMItemViewDetail(product);

    gtmPushItemDetailClick({
      currency: detailViewData.currency,
      item: detailViewData.item,
    });
  };

  const handleProductClick = () => {
    setProductCategoryIdToLocalStorage(); // need to set correct product category for gtm
    pushGtmItemDetailClick();
  };

  const handleReviewClick: MouseEventHandler = (e) => {
    e.preventDefault();
    pushGtmItemDetailClick();
    push(`${product.href}#reviews`);
  };

  const toggleBannerModal = () => {
    setOpenBannerModal((open) => !open);
  };

  const isDailyDealProduct =
    product.isPrimaryPage && product.label === DAILY_DEAL_PRODUCT_LABEL;

  return (
    <div
      ref={ref}
      className={clsx(
        styles.root,
        !isSizeSelectVisible && styles.rootHoverEffect,
      )}
    >
      {isDailyDealProduct && (
        <Image
          className={styles.badge}
          src={DAILY_DEAL_PRODUCT_BADGE_URL}
          alt={t('product.tile.tages-tipp')}
        />
      )}

      {isMobile && (
        <WishlistIcon
          className={styles.wishlist}
          productName={product.name}
          variantId={product?.variants?.[0].id}
        />
      )}

      <Link
        href={product.href}
        className={clsx(styles.wrapperLink, styles.container)}
        onClick={handleProductClick}
      >
        <div className={styles.top}>
          {!isDailyDealProduct && product.label && (
            <P className={styles.label}>{product.label}</P>
          )}
          {isTablet && (
            <>
              <DeliveryStatus status={DeliveryStatusType.AVAILABLE} />
              {!!currentPrice && !!oldPrice && (
                <Discount
                  currentPrice={currentPrice}
                  oldPrice={oldPrice}
                  className={styles.discount}
                />
              )}
            </>
          )}
        </div>

        {isTablet && (
          <div className={styles.hoveredTick}>
            <DeliveryStatus hovered status={DeliveryStatusType.AVAILABLE} />
          </div>
        )}

        <ProductImage hash={product.coverImage} className={styles.cover} />
        {!isSizeSelectVisible && (
          <>
            <div className={styles.center}>
              <div className={styles.reviews}>
                <ReviewsRating
                  onClick={handleReviewClick}
                  url={product.href}
                  size="small"
                  value={product.ratingScore}
                  count={product.ratingCount}
                />
                {Boolean(hasVideo) && (
                  <>
                    <Icon
                      type={IconType.play}
                      className={styles.play}
                      onClick={toggleModal}
                    />
                    <Modal
                      open={openModal}
                      onClose={toggleModal}
                      className={styles.playerTest}
                    >
                      <ProductVideoBlock videos={product.video ?? []} />
                    </Modal>
                  </>
                )}
              </div>
              <div className={styles.textContainer}>
                <P className={styles.category}>
                  <Span>{[category, brand].filter((a) => a).join(' - ')}</Span>
                </P>
                <P className={styles.name}>
                  <ParseBBCode content={product.name} />
                </P>

                {!isMobile && (
                  <>
                    <ProductMaterialInfo
                      size={product.size}
                      composedMaterials={product.composedMaterials}
                    />
                    {product.size && <ProductSize size={product.size} />}
                  </>
                )}

                {isTablet && activeVariantPrice?.reference && (
                  <ProductReferencePrice
                    price={activeVariantPrice}
                    className={styles.measurable}
                  />
                )}
              </div>

              {!!currentPrice && !!oldPrice && (
                <Discount
                  currentPrice={currentPrice}
                  oldPrice={oldPrice}
                  forMobile
                />
              )}
            </div>

            {isMounted && (
              <ProductBullets
                bullets={product.bullets}
                className={clsx(styles.productBullet, styles.hiddenText)}
              />
            )}
          </>
        )}
      </Link>
      {isSizeSelectVisible && (
        <SelectSizeButtons
          open={isSizeSelectVisible}
          onClose={toggleSizeSelectVisibility}
          sizes={product.size}
          onClick={(size) => {
            size.referenceKey &&
              addToCart(size.referenceKey, 1, pageType, product.limitForCart);
            upsellingBanner && toggleBannerModal();
            setProductCategoryIdToLocalStorage(); // need to set correct product category for gtm
            toggleSizeSelectVisibility();
          }}
        />
      )}
      {!isSizeSelectVisible && (
        <div className={clsx(styles.bottom, styles.container)}>
          <div className={styles.price}>
            <OldPrice
              amount={product.price.recommendedRetailPrice}
              currencyCode={product.price.currencyCode}
              className={styles.oldPrice}
            />
            <Price
              amount={currentPrice}
              currencyCode={product.price.currencyCode}
              className={clsx(
                styles.currentPrice,
                displaySalePrices && styles.salePrice,
              )}
            />
          </div>

          {isTablet && (
            <>
              <Button
                className={styles.button}
                onClick={() => {
                  if (hasSizes) {
                    toggleSizeSelectVisibility();
                  } else if (product.referenceKey) {
                    toggleBannerModal();
                    addToCart(
                      product.referenceKey,
                      1,
                      pageType,
                      product.limitForCart,
                    );
                    setProductCategoryIdToLocalStorage(); // need to set correct product category for gtm
                  }
                }}
              >
                {t('product.tile.button')}
              </Button>
              <Vat
                rate={product.taxRatePercent}
                referenceKey={product.referenceKey}
              />
            </>
          )}
        </div>
      )}

      {upsellingBanner && isTablet && (
        <UpsellingBannerModal
          open={openBannerModal}
          onClose={toggleBannerModal}
          banner={upsellingBanner}
        />
      )}
    </div>
  );
});
